import * as React from 'react';

import { withPrefix } from 'gatsby';
import { useLocation } from '@reach/router';

import clsx from 'clsx';
import { Helmet, HelmetProvider } from 'react-helmet-async';

import { LiveEditor, LiveError, LivePreview, LiveProvider } from 'react-live';

import DemoThemes from '../../../themes/demos/DemoThemes';
import CustomHeadline from '../../demos/CustomHeadline';
import useMockArray from '../../hooks/useMockArray';

import '../../../assets/unifiWebDocs.css';
import '../../../assets/webDemos.css';

import PrismTheme from './PrismTheme';

import {
  Add,
  Check,
  Close,
  Delete,
  DeleteOutlined,
  DeleteRounded,
  DeleteSharp,
  DeleteTwoTone,
  Done,
  Edit,
  Error,
  ExpandMore,
  Face,
  Favorite,
  FavoriteBorder,
  Folder,
  Inbox,
  Info,
  LocationOn,
  Mail,
  Menu as MenuIcon,
  More,
  MoreVert,
  Navigation,
  Person,
  Restore,
  Save,
  Search,
  Share,
  Visibility,
  VisibilityOff,
} from '@material-ui/icons';

import {
  AppBar,
  Autocomplete,
  Avatar,
  Badge,
  Button,
  ButtonBase,
  ButtonGroup,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  createGenerateClassName,
  createStyles,
  createTheme,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  Fab,
  Fade,
  FilledInput,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Grow,
  Hidden,
  Icon,
  IconButton,
  Input,
  InputAdornment,
  InputBase,
  InputLabel,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  MobileStepper,
  NativeSelect,
  Notification,
  OutlinedInput,
  Pagination,
  Panel,
  PanelContent,
  PanelHeader,
  Paper,
  Portal,
  Radio,
  RadioGroup,
  RootRef,
  Select,
  Slide,
  Snackbar,
  SnackbarContent,
  Step,
  StepLabel,
  Stepper,
  SvgIcon,
  SwipeableDrawer,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Theme,
  Toolbar,
  Tooltip,
  Typography,
  UnifiThemeProvider,
  withStyles,
  WithStyles,
  Zoom,
} from '@nelnet/unifi-components-react';

const scope = {
  AppBar,
  Autocomplete,
  Avatar,
  Badge,
  Button,
  ButtonBase,
  ButtonGroup,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  createGenerateClassName,
  createTheme,
  CssBaseline,
  CustomHeadline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  Fab,
  Fade,
  FilledInput,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  Grid,
  Grow,
  Hidden,
  Icon,
  IconButton,
  Input,
  InputAdornment,
  InputBase,
  InputLabel,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  MobileStepper,
  NativeSelect,
  Notification,
  OutlinedInput,
  Pagination,
  Panel,
  PanelContent,
  PanelHeader,
  Paper,
  Portal,
  Radio,
  RadioGroup,
  RootRef,
  Select,
  Slide,
  Snackbar,
  SnackbarContent,
  Step,
  StepLabel,
  Stepper,
  SvgIcon,
  SwipeableDrawer,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  UnifiThemeProvider,
  Toolbar,
  Tooltip,
  Typography,
  withStyles,
  useMockArray,
  Zoom,
  Add,
  Check,
  Close,
  Delete,
  DeleteOutlined,
  DeleteRounded,
  DeleteTwoTone,
  DeleteSharp,
  Done,
  Edit,
  Error,
  ExpandMore,
  Face,
  Favorite,
  FavoriteBorder,
  Folder,
  Inbox,
  Info,
  LocationOn,
  Mail,
  MenuIcon,
  More,
  MoreVert,
  Navigation,
  Person,
  Restore,
  Save,
  Search,
  Share,
  Visibility,
  VisibilityOff,
};

const styles = ({ breakpoints }: Theme) =>
  createStyles({
    root: {
      marginTop: '32px',
    },
    gridWrapper: {
      maxWidth: '100%',
      width: 'calc(100vw - 48px)',
      [breakpoints.up('md')]: {
        width: 'calc(100vw - 316px)',
      },
    },
    preview: {
      fontFamily: 'Open Sans',
      border: '1px solid #ccc',
      maxHeight: '60vh',
      overflow: 'scroll',
      '& *': {
        whiteSpace: 'normal',
      },
      '& > div': {
        padding: '24px',
      },
    },
    editorWrap: {
      display: 'flex',
      maxHeight: '60vh',
      position: 'relative',
    },
    editorInlineWrap: {
      maxWidth: '100%',
      maxHeight: '60vh',
      position: 'relative',
    },
    editor: {
      height: '100%',
      minWidth: '100%',
      display: 'block',
      overflow: 'auto !important',
      margin: 0,
      padding: '16px',
      backgroundColor: '#f3f3f3',
      border: '1px solid #ccc',
      borderLeftWidth: '0',
      [breakpoints.down('xs')]: {
        borderTopWidth: '0',
        borderLeftWidth: '1px',
      },
    },
    editorBlock: {
      borderLeftWidth: '1px',
      '&:focus': {
        outline: 'none',
      },
    },
    editorFocus: {
      position: 'absolute',
      left: '-10000px',
      top: 'auto',
      width: '1px',
      height: '1px',
      overflow: 'hidden',
      '&:focus': {
        width: 'auto',
        height: 'auto',
        left: '1em',
        top: '-1.25em',
        zIndex: 1100,
        backgroundColor: '#fff',
      },
    },
  });

interface IPrismicCodeProps extends WithStyles<typeof styles> {
  children?: string;
  language?: string;
}

const PrismicCode: React.FunctionComponent<IPrismicCodeProps> = ({
  children,
  language,
  classes,
}) => {
  const { pathname } = useLocation();

  const codeFocusButton = React.createRef<HTMLDivElement>();
  const codeFocusEl = React.createRef<HTMLDivElement>();

  React.useEffect(() => {
    handleHeight();
  });

  const handleHeight = () => {
    const editor = codeFocusEl.current!;

    if (editor) {
      const editorHeight = editor.clientHeight;
      const textarea = editor.querySelector('textarea');
      const preHeight = editor.querySelector('pre').clientHeight;

      textarea.tabIndex = -1;
      textarea.style.height = editorHeight > preHeight ? `${editorHeight}px` : `${preHeight}px`;
    }
  };

  const handleClick: React.MouseEventHandler = (event) => {
    event.preventDefault();

    const node = codeFocusEl.current!;

    node.querySelector('textarea').focus();
  };

  const handleKeys = (event: KeyboardEvent) => {
    const button = codeFocusButton.current!;

    if (event.key === 'Escape') {
      button.focus();
    } else {
      handleHeight();
    }
  };

  let code = children || '';
  const jsxCode = !language || language === 'language-jsx';
  const htmlCode = language === 'language-html' || pathname.includes('web');
  const codeOnly = code.startsWith('[block]');
  const displayPreview = !codeOnly && (jsxCode || htmlCode);
  const previewWrapClass = htmlCode ? 'component-preview' : '';
  const editorBlockClass = displayPreview ? '' : classes.editorBlock;
  const editorWrapClass = displayPreview ? classes.editorWrap : classes.editorInlineWrap;
  let theme;

  let width: 6 | 12 | 'auto' = displayPreview ? 6 : 'auto';

  if (code && typeof code === 'string') {
    if (code.startsWith('[fullwidth]')) {
      width = 12;
      code = code.replace('[fullwidth]', '');
    }

    if (code.startsWith(':theme')) {
      const themeVal = /:theme\[(\w.*)\]/;
      theme = code.match(themeVal) || '';
      code = code.replace(theme[0], '');
    }

    if (code.startsWith('[block]')) {
      code = code.replace('[block]', '');
    }
  }

  const codeContent = code || '';

  const liveCodeRender = (
    <LiveProvider
      code={codeContent}
      scope={scope}
      className={classes.root}
      noInline={!displayPreview}
      disabled={!displayPreview}
      theme={PrismTheme}
    >
      <Grid container className={clsx(classes.gridWrapper, 'react-live')}>
        {displayPreview && (
          <Grid item xs={12} sm={width} className={clsx(classes.preview, previewWrapClass)}>
            <LivePreview />
          </Grid>
        )}
        <Grid item xs={12} sm={width} className={editorWrapClass}>
          {displayPreview ? (
            <Button
              className={classes.editorFocus}
              variant="outlined"
              color="secondary"
              size="small"
              onClick={handleClick}
              buttonRef={codeFocusButton}
            >
              Enter Code Editor
            </Button>
          ) : null}
          <RootRef rootRef={codeFocusEl}>
            <LiveEditor
              className={clsx(classes.editor, editorBlockClass)}
              onKeyDown={(e) => handleKeys(e as unknown as KeyboardEvent)}
            />
          </RootRef>
        </Grid>
      </Grid>
      {displayPreview && <LiveError />}
    </LiveProvider>
  );

  let unifiWebStyle;

  if (htmlCode) {
    unifiWebStyle = (
      <HelmetProvider>
        <Helmet>
          <link rel="stylesheet" type="text/css" href="../../assets/unifiWebDocs.css" />
          <link rel="stylesheet" type="text/css" href="../../assets/webDemos.css" />
          <script src={withPrefix('unifi.min.js')} type="text/javascript" />
        </Helmet>
      </HelmetProvider>
    );
  }

  if (code) {
    if (theme) {
      return (
        <>
          <DemoThemes theme={theme[1]}>{liveCodeRender}</DemoThemes>
        </>
      );
    }

    return (
      <>
        {unifiWebStyle}
        {liveCodeRender}
      </>
    );
  }

  return <>{children}</>;
};

export default withStyles(styles)(PrismicCode);
